/**
 * Holi cards — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_shivratri_holi`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const holiCardsPack: ToilePack = {
    slug: 'holi-cards',
    category: 'faith',
    name: 'Holi cards',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'splash', label: 'Colour splash', subject: 'a vertical card with bursts of pink, yellow, green and blue powder across the top, "Happy Holi" lettered large in the middle and a blank panel below for a name'},
        {key: 'pots', label: 'Colour pots', subject: 'a vertical card with three clay pots of gulal and a pichkari along the bottom edge, "Happy Holi" lettered above and a thin border of marigolds'},
        {key: 'playful', label: 'Bura na mano', subject: 'a vertical card with the words "Bura Na Mano, Holi Hai!" lettered in bold, a splatter of colour round them and a small pichkari in one corner'},
    ],
    styles: [
        {key: 'watercolor', label: 'Watercolor', style: 'loose watercolor washes with soft bleeding edges and speckled powder splatter', palette: 'bright pink, yellow, green and blue on white paper', framing: 'centered on the card with a white margin on every side'},
        {key: 'flat', label: 'Flat vector', style: 'flat vector illustration with bold clean shapes and crisp edges', palette: 'five-colour palette of magenta, yellow, green, blue and orange on a cream ground', framing: 'the design filling the card inside an even margin'},
        {key: 'chalk', label: 'Chalk pastel', style: 'chalk pastel on dark paper with powdery smudged colour and visible grain', palette: 'neon pink, yellow, cyan and green on a charcoal ground', framing: 'full-bleed to the card edge with the lettering kept inside a safe margin'},
    ],
    directions: ['for kids', 'office colleagues', 'gold foil look'],
    directionHint: 'try: for kids',
    seo: {
        title: 'Holi cards: colour splash, colour pots, Bura Na Mano',
        metaDescription: 'Nine Holi greeting cards to print or send as an image: colour splashes, clay pots of gulal and a Bura Na Mano card in three styles.',
        h1: 'Holi cards: colour splash, colour pots and Bura Na Mano',
        tagline: 'Nine greeting cards, three designs each in watercolour, flat vector and chalk pastel.',
        intro: 'Holi greeting cards to print, fold or send to family and friends. The cards carry the greeting in clear letters among splashes of gulal: bursts of colour with a blank panel for a name, three clay pots of colour with a border of marigolds, or the words Bura Na Mano and Holi Hai. The styles are soft watercolour, flat vector and chalk pastel on dark paper. Type a different audience and the same styles carry it.',
        useCases: [
            'A card to print and fold for family',
            'A greeting image to send on a phone',
            'A card for a classroom or an office',
            'A card for a shop or a neighbourhood group',
        ],
        faq: [
            {q: 'Can I print these at home?', a: 'Yes. Each card is a single page with a clear margin, ready to print and fold.'},
            {q: 'What is the difference between the three styles?', a: 'Watercolour is soft washes on white paper, flat vector is bold clean shapes on cream, and chalk pastel is powdery colour on a charcoal ground.'},
            {q: 'Can I add my name to a card?', a: 'The colour splash card leaves a blank panel below the greeting for a name.'},
        ],
    },
}
